// File: src/components/MatchFlow/InstructionsBottomSheet.jsx
import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import { LuUsers, LuThumbsUp, LuMapPin, LuSparkles } from 'react-icons/lu';

// Step illustrations (width 225, no grey box)
import { ReactComponent as Step1Illustration } from '../../assets/Illustrations/Search User 3 Streamline Milano.svg';
import { ReactComponent as Step2Illustration } from '../../assets/Illustrations/Approval 2 Streamline Milano.svg';
import { ReactComponent as Step3Illustration } from '../../assets/Illustrations/Tracking Users 2 Streamline Milano.svg';
import { ReactComponent as Step4Illustration } from '../../assets/Illustrations/Business Deal 2 Streamline Milano.svg';

const isPWA = () =>
  (window.matchMedia?.('(display-mode: standalone)').matches) ||
  window.navigator.standalone === true;

/**
 * InstructionsBottomSheet (Speed Match)
 * - Visual parity with RevealInstructionsBottomSheet
 * - Replaces icon “hero card” with SVG illustrations (no grey box)
 * - Icon imports left in place for minimal code disruption
 * - No preferences slide
 */
export default function InstructionsBottomSheet({ visible = false, onClose }) {
  const [step, setStep] = useState(0); // zero-based
  const scrollRef = useRef(0);

  // Slides for Speed Match (titles/descriptions unchanged; artwork swapped)
  const slides = useMemo(
    () => [
      {
        Icon: LuUsers, // kept (not used for hero)
        Illustration: Step1Illustration, // STEP 1 artwork
        title: 'Tap “Find Match”',
        description:
          'We’ll start searching the room to find the best available match for you.',
        accent: '#0b0b0b',
        badgeBg: 'transparent',
      },
      {
        Icon: LuThumbsUp,
        Illustration: Step2Illustration, // STEP 2 artwork
        title: 'Accept Your Match',
        description:
          'When a match appears, tap Accept to confirm and continue.',
        accent: '#111827',
        badgeBg: 'transparent',
      },
      {
        Icon: LuMapPin,
        Illustration: Step3Illustration // STEP 3 artwork
          ,
        title: 'Navigate & Verify',
        description:
          'Head to the meeting spot and verify each other’s code to begin.',
        accent: '#0b0b0b',
        badgeBg: 'transparent',
      },
      {
        Icon: LuSparkles,
        Illustration: Step4Illustration, // STEP 4 artwork
        title: 'Enjoy the Conversation',
        description:
          'Use our prompts to break the ice and see where the chat goes.',
        accent: '#000',
        badgeBg: 'transparent',
      },
    ],
    []
  );

  const total = slides.length;
  const isLast = step === total - 1;
  const current = slides[step];

  // Reset step on open
  useEffect(() => {
    if (visible) setStep(0);
  }, [visible]);

  // Optional parity: lightweight socket heartbeat (kept)
  const userId =
    typeof window !== 'undefined' ? localStorage.getItem('userId') : null;
  useEffect(() => {
    if (!visible) return;
    const socket = io(`${process.env.REACT_APP_API_URL}`);
    socket.on('connect', () =>
      console.log('[CLIENT] InstructionsBottomSheet socket connected =>', socket.id)
    );
    const timerId = setInterval(() => {
      if (navigator && navigator.geolocation && userId) {
        navigator.geolocation.getCurrentPosition((pos) => {
          const sessionId = localStorage.getItem('sessionId');
          socket.emit('locationUpdate', {
            userId,
            sessionId,
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
          });
        });
      }
    }, 60_000);
    return () => {
      clearInterval(timerId);
      socket.disconnect();
    };
  }, [visible, userId]);

  // Overlay + theme color parity (kept)
  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (!visible) return;
    scrollRef.current = window.scrollY || window.pageYOffset;
    document.body.style.top = `-${scrollRef.current}px`;
    document.body.classList.add('overlay-active');
    document.documentElement.classList.add('overlay-active');
    document.getElementById('root')?.classList.add('overlay-active');
    if (meta) meta.content = '#999998';
    return () => {
      const y = scrollRef.current;
      document.body.classList.remove('overlay-active');
      document.documentElement.classList.remove('overlay-active');
      document.getElementById('root')?.classList.remove('overlay-active');
      document.body.style.top = '';
      window.scrollTo(0, y);
      if (meta) meta.content = '#ffffff';
    };
  }, [visible]);

  // Navigation (kept)
  const handleClose = useCallback(() => onClose?.(), [onClose]);
  const handlePrev = useCallback(() => {
    if (step === 0) handleClose();
    else setStep((s) => s - 1);
  }, [step, handleClose]);
  const handleSkip = useCallback(() => handleClose(), [handleClose]);
  const handleNextOrDone = useCallback(() => {
    if (!isLast) setStep((s) => s + 1);
    else handleClose();
  }, [isLast, handleClose]);

  // Swipe + keyboard (kept)
  const touchStartX = useRef(null);
  const onTouchStart = (e) =>
    (touchStartX.current = e.touches?.[0]?.clientX ?? null);
  const onTouchEnd = (e) => {
    const start = touchStartX.current;
    const end = e.changedTouches?.[0]?.clientX ?? null;
    if (start == null || end == null) return;
    const delta = end - start;
    if (Math.abs(delta) > 40) {
      if (delta > 0) handlePrev();
      else handleNextOrDone();
    }
  };
  useEffect(() => {
    if (!visible) return;
    const onKey = (e) => {
      if (e.key === 'Escape') handleClose();
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNextOrDone();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [visible, handleClose, handlePrev, handleNextOrDone]);

  if (!visible) return null;

  const progress = (step + 1) / total;

  return (
    <div style={S.backdrop} onClick={handleSkip}>
      <div
        style={{
          ...S.sheet,
          paddingBottom: `calc(${isPWA() ? 20 : 12}px + env(safe-area-inset-bottom, 0px))`,
        }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Speed Match quick tour"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {/* Top bar */}
        <div style={S.topBar}>
          <button
            type="button"
            onClick={handlePrev}
            aria-label={step === 0 ? 'Close' : 'Back'}
            style={S.iconBtn}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              aria-hidden="true"
              style={{ display: 'block' }}
            >
              {step === 0 ? (
                <path
                  d="M18 6L6 18M6 6l12 12"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              ) : (
                <path
                  d="M15 18l-6-6 6-6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  fill="none"
                />
              )}
            </svg>
          </button>

          <div style={S.stepPill}>
            <span style={S.stepPillPrefix}>Step&nbsp;</span>
            <strong style={S.stepPillNum}>{step + 1}</strong>
            <span style={S.stepPillTotal}>&nbsp;/&nbsp;{total}</span>
          </div>

          <button type="button" onClick={handleSkip} style={S.skipLink}>
            Skip
          </button>
        </div>

        {/* Progress bar */}
        <div style={S.progressTrack} aria-hidden>
          <div style={{ ...S.progressFill, width: `${progress * 100}%` }} />
        </div>

        {/* Hero illustration — no grey box, width 225 */}
        <div style={S.hero}>
          <current.Illustration
            style={{ width: 275, height: 'auto', display: 'block' }}
          />
        </div>

        {/* Copy */}
        <div style={S.copy}>
          <h3 style={S.title}>{current.title}</h3>
          <p style={S.desc}>{current.description}</p>
        </div>

        {/* CTA */}
        <div style={S.ctaRow}>
          <button type="button" onClick={handleNextOrDone} style={S.primaryBtn}>
            {isLast ? 'Got it' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* Styles — largely unchanged; hero box is removed/neutralized */
const S = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.40)',
    zIndex: 9999,
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },
  sheet: {
    width: '100%',
    maxWidth: 440,
    margin: '0 10px 10px',
    background: '#ffffff',
    borderRadius: 12,
    boxShadow: '0 8px 28px rgba(0,0,0,0.25)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    padding: 16,
  },
  topBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 40,
  },
  iconBtn: {
    background: 'transparent',
    border: 0,
    width: 32,
    height: 32,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 0,
    padding: 0,
    color: '#0b0b0b',
    borderRadius: 8,
    cursor: 'pointer',
  },
  skipLink: {
    background: 'transparent',
    border: 0,
    padding: '8px 10px',
    color: '#6b7280',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    borderRadius: 8,
  },
  stepPill: {
    background: '#f3f4f6',
    color: '#0b0b0b',
    borderRadius: 999,
    padding: '6px 12px',
    fontSize: 12,
    display: 'flex',
    alignItems: 'center',
    lineHeight: 1,
  },
  stepPillPrefix: { fontWeight: 500, color: '#6b7280' },
  stepPillNum: { fontWeight: 800 },
  stepPillTotal: { color: '#6b7280', fontWeight: 600 },

  progressTrack: {
    height: 3,
    background: '#eef0f2',
    borderRadius: 3,
    overflow: 'hidden',
    marginTop: 12,
  },
  progressFill: {
    height: '100%',
    background: '#0b0b0b',
    transition: 'width .25s ease',
  },

  // Center the illustration; no grey box
  hero: {
    marginTop: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  // Old heroCard/iconBadge retained but unused (left for minimal diff)
  heroCard: {
    display: 'none',
  },
  iconBadge: {
    display: 'none',
  },

  copy: {
    textAlign: 'left',
    padding: '14px 2px 0',
  },
  title: {
    fontSize: 20,
    lineHeight: 1.25,
    letterSpacing: '-0.01em',
    fontWeight: 800,
    color: '#0b0b0b',
    margin: 0,
  },
  desc: {
    marginTop: 6,
    marginBottom: 0,
    fontSize: 14,
    lineHeight: 1.5,
    color: '#4b5563',
  },

  ctaRow: {
    display: 'flex',
    gap: 8,
    marginTop: 14,
  },
  primaryBtn: {
    flex: 1,
    background: '#0b0b0b',
    color: '#fff',
    border: '1px solid #0b0b0b',
    borderRadius: 10,
    padding: '12px 14px',
    fontSize: 13,
    fontWeight: 800,
    cursor: 'pointer',
    marginTop: 8,
  },
};
